import {
  DeleteOutlined,
  DownloadOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import {
  EProductSafetyDocumentKind,
  type IProductBuyer,
  type IProductSafetyDocument,
  type ITranslation,
} from '@mahawi/eshop-common/src/types';
import {
  Alert,
  Button,
  Col,
  Descriptions,
  Divider,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { ENotificationSeverity } from '../../../admin-types';
import {
  EDateRangePreset,
  type IDateRange,
  type IIsoDateRange,
  resolveDateRange,
  toIsoDateRange,
} from '../../fragments/date-range';
import { EMPTY_VALUE } from '../../fragments/empty-value';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type NotifyType, useNotify } from '../../notification';
import { type RootState } from '../../reducers';
import {
  manufacturersLoadAll,
  productBuyersLoad,
  productSafetyDocumentDelete,
  productSafetyLoad,
  productSafetyUpdate,
  responsiblePersonsLoadAll,
} from '../../reducers/product-safety/reducer';
import { SPACING } from '../../theme/tokens';
import DateRangeFilter from '../date-range-filter';
import OperatorSelect from '../product-safety/operator-select';
import Table from '../table';

const { Dragger } = Upload;

export const PRODUCT_SAFETY_DOCUMENT_MAX_SIZE = 20 * 1024 * 1024;
const NEUTRAL_LANGUAGE = '';

interface IForm {
  manufacturerUuid: string | null;
  responsiblePersonUuid: string | null;
  safetyInformation: string;
}

const escapeCsv = (value: string | number | null): string =>
  `"${String(value ?? '').replace(/"/g, '""')}"`;

export const buyersToCsv = (buyers: IProductBuyer[]): string =>
  [
    ['orderUuid', 'createdAt', 'name', 'surname', 'email', 'phone', 'count'],
    ...buyers.map((buyer: IProductBuyer) => [
      buyer.orderUuid,
      buyer.createdAt,
      buyer.name,
      buyer.surname,
      buyer.email,
      buyer.phone,
      buyer.count,
    ]),
  ]
    .map((row: (string | number | null)[]): string =>
      row.map(escapeCsv).join(','),
    )
    .join('\n');

const KIND_OPTIONS = Object.values(EProductSafetyDocumentKind).map(
  (kind: EProductSafetyDocumentKind) => ({ label: kind, value: kind }),
);

function ProductSafety(): React.ReactElement {
  const notify: NotifyType = useNotify();
  const dispatch = useAppDispatch();
  const productUuid = useSelector((s: RootState) => s.Product.uuid);
  const languageCode = useSelector(
    (s: RootState) => s.Language.languageType?.code,
  );
  const languages = useSelector((s: RootState) => s.Language.languages);
  const productSafety = useSelector(
    (s: RootState) => s.ProductSafety.productSafety,
  );
  const failure = useSelector((s: RootState) => s.ProductSafety.failure);
  const buyers = useSelector((s: RootState) => s.ProductSafety.buyers);
  const inProcess = useSelector((s: RootState) => s.ProductSafety.inProcess);
  const manufacturers = useSelector(
    (s: RootState) => s.ProductSafety.manufacturers,
  );
  const responsiblePersons = useSelector(
    (s: RootState) => s.ProductSafety.responsiblePersons,
  );

  const [form] = Form.useForm<IForm>();
  const [documentKind, setDocumentKind] = useState<EProductSafetyDocumentKind>(
    EProductSafetyDocumentKind.INSTRUCTIONS,
  );
  const [documentLanguage, setDocumentLanguage] =
    useState<string>(NEUTRAL_LANGUAGE);
  const [range, setRange] = useState<IDateRange>(
    resolveDateRange(EDateRangePreset.LAST_90_DAYS),
  );

  useEffect((): void => {
    if (productUuid) {
      dispatch(productSafetyLoad(productUuid));
    }

    dispatch(manufacturersLoadAll());
    dispatch(responsiblePersonsLoadAll());
  }, [dispatch, productUuid]);

  useEffect((): void => {
    if (!productSafety) {
      return;
    }

    form.setFieldsValue({
      manufacturerUuid: productSafety.manufacturerOverrideUuid,
      responsiblePersonUuid: productSafety.responsiblePersonOverrideUuid,
      safetyInformation:
        productSafety.safetyInformation.find(
          (translation: ITranslation): boolean =>
            translation.code === languageCode,
        )?.value ?? '',
    });
  }, [form, productSafety, languageCode]);

  const onFinish = useCallback(
    (values: IForm): void => {
      if (!productUuid) {
        return;
      }

      dispatch(
        productSafetyUpdate({
          productUuid,
          manufacturerUuid: values.manufacturerUuid ?? null,
          responsiblePersonUuid: values.responsiblePersonUuid ?? null,
          safetyInformation: languageCode
            ? [{ code: languageCode, value: values.safetyInformation ?? '' }]
            : [],
        }),
      );
    },
    [dispatch, productUuid, languageCode],
  );

  const languageOptions = useMemo(
    () => [
      { label: 'All languages (pictograms only)', value: NEUTRAL_LANGUAGE },
      ...(languages ?? []).map((language) => ({
        label: language.name,
        value: language.code,
      })),
    ],
    [languages],
  );

  const exportBuyers = useCallback((): void => {
    const blob = new Blob([buyersToCsv(buyers ?? [])], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `product-buyers-${productUuid ?? 'product'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [buyers, productUuid]);

  if (!productUuid) {
    return <Typography.Text>Product is not loaded.</Typography.Text>;
  }

  const documentColumns = [
    {
      title: 'Language',
      dataIndex: 'languageCode',
      key: 'languageCode',
      render: (code: string | null): string => code ?? 'all',
    },
    { title: 'Kind', dataIndex: 'kind', key: 'kind' },
    {
      title: 'File',
      dataIndex: 'filename',
      key: 'filename',
      render: (
        _value: string,
        document: IProductSafetyDocument,
      ): React.ReactElement => (
        <a href={document.url} target="_blank" rel="noreferrer">
          {document.filename}
        </a>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      render: (size: number): string => `${(size / 1024).toFixed(0)} kB`,
    },
    {
      title: '',
      key: 'actions',
      render: (
        _value: string,
        document: IProductSafetyDocument,
      ): React.ReactElement => (
        <Popconfirm
          title={`Delete ${document.filename}?`}
          okText="Delete"
          okButtonProps={{ danger: true }}
          disabled={inProcess}
          onConfirm={(): void => {
            dispatch(
              productSafetyDocumentDelete({
                productUuid,
                documentUuid: document.uuid,
              }),
            );
          }}
        >
          <Tooltip title="Delete document">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={inProcess}
            />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  const buyerColumns = [
    { title: 'Order', dataIndex: 'orderUuid', key: 'orderUuid' },
    { title: 'Created', dataIndex: 'createdAt', key: 'createdAt' },
    {
      title: 'Name',
      key: 'name',
      render: (_value: string, buyer: IProductBuyer): string =>
        `${buyer.name} ${buyer.surname}`,
    },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    { title: 'Count', dataIndex: 'count', key: 'count' },
  ];

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      {failure ? <Alert type="warning" showIcon title={failure} /> : null}

      <Descriptions
        bordered
        size="small"
        column={1}
        items={[
          {
            key: 'manufacturer',
            label: 'Manufacturer',
            children: productSafety?.manufacturer ? (
              <Space>
                {productSafety.manufacturer.name} (
                {productSafety.manufacturer.countryType.isoCode2})
                <Tag>{productSafety.manufacturerSource}</Tag>
              </Space>
            ) : (
              <Tag color="red">missing</Tag>
            ),
          },
          {
            key: 'eu-responsible-person',
            label: 'EU responsible person',
            children: productSafety?.responsiblePerson ? (
              <Space>
                {productSafety.responsiblePerson.name} (
                {productSafety.responsiblePerson.role})
                <Tag>{productSafety.responsiblePersonSource}</Tag>
              </Space>
            ) : (
              <Tag>none</Tag>
            ),
          },
        ]}
      />

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={SPACING.lg}>
          <Col span={12}>
            <Form.Item name="manufacturerUuid" label="Manufacturer override">
              <OperatorSelect
                operators={manufacturers}
                disabled={inProcess}
                placeholder="Use the brand manufacturer"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="responsiblePersonUuid"
              label="EU responsible person override"
            >
              <OperatorSelect
                operators={responsiblePersons}
                disabled={inProcess}
                placeholder="Use the brand responsible person"
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="safetyInformation"
          label={`Warnings and safety information (${languageCode ?? EMPTY_VALUE})`}
        >
          <Input.TextArea rows={6} disabled={inProcess || !languageCode} />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={inProcess}>
          Save
        </Button>
      </Form>

      <Divider />

      <Typography.Title level={4}>Documents</Typography.Title>
      <Space size={SPACING.lg} align="start">
        <Select
          style={{ width: 200 }}
          value={documentKind}
          options={KIND_OPTIONS}
          onChange={(value: EProductSafetyDocumentKind): void =>
            setDocumentKind(value)
          }
        />
        <Select
          style={{ width: 260 }}
          value={documentLanguage}
          options={languageOptions}
          onChange={(value: string): void => setDocumentLanguage(value)}
        />
      </Space>
      <Dragger
        name="document"
        accept="application/pdf"
        multiple={false}
        showUploadList={false}
        action={`/api/product/${productUuid}/safety-document`}
        data={{ kind: documentKind, languageCode: documentLanguage }}
        beforeUpload={(file): boolean => {
          const isAccepted =
            file.type === 'application/pdf' &&
            file.size <= PRODUCT_SAFETY_DOCUMENT_MAX_SIZE;

          if (!isAccepted) {
            notify(
              ENotificationSeverity.ERROR,
              'Upload a PDF of at most 20 MB.',
            );
          }

          return isAccepted;
        }}
        onChange={(info): void => {
          if (info.file.status === 'done') {
            notify(
              ENotificationSeverity.SUCCESS,
              `${info.file.name} uploaded.`,
            );
            dispatch(productSafetyLoad(productUuid));
          } else if (info.file.status === 'error') {
            notify(
              ENotificationSeverity.ERROR,
              `${info.file.name} upload failed.`,
            );
          }
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag a PDF here to upload</p>
      </Dragger>
      <Table
        columns={documentColumns}
        dataSource={(productSafety?.documents ?? []).map(
          (document: IProductSafetyDocument) => ({
            ...document,
            key: document.uuid,
          }),
        )}
        isLoading={inProcess && !productSafety}
      />

      <Divider />

      <Typography.Title level={4}>Buyers</Typography.Title>
      <Space size={SPACING.lg} align="start">
        <DateRangeFilter value={range} onChange={setRange} />
        <Button
          disabled={inProcess}
          onClick={(): void => {
            const { from, to }: IIsoDateRange = toIsoDateRange(range);

            dispatch(productBuyersLoad({ productUuid, from, to }));
          }}
        >
          Find buyers
        </Button>
        <Button
          icon={<DownloadOutlined />}
          disabled={!buyers || buyers.length === 0}
          onClick={exportBuyers}
        >
          CSV
        </Button>
      </Space>
      <Table
        columns={buyerColumns}
        dataSource={(buyers ?? []).map((buyer: IProductBuyer) => ({
          ...buyer,
          key: buyer.orderUuid,
        }))}
        isLoading={inProcess && buyers === undefined && range !== null}
      />
    </Space>
  );
}

export default ProductSafety;
